import { FC, useEffect, useState } from 'react';
import { TQuizStatsOverview } from '../../../../types/analytics.types';
import AnalyticsService from '../../../../services/analytics.service';

interface StatCardProps {
  title: string;
  value: string | number;
  growth: number;
}

const STAT_CARD_BASE_CLASSES =
  'border-[#E2E8F0] border bg-white px-4 py-3 hover:border-[#020817] transition-all cursor-pointer rounded-lg h-[120px] flex-grow';
const TITLE_CLASSES = 'text-[14px] font-semibold';
const VALUE_CLASSES = 'font-semibold text-[24px] leading-[20px] mt-7';
const GROWTH_CLASSES = 'text-green-600 text-[12px] leading-[10px]';
const GROWTH_DECLINE_CLASSES = 'text-red-600 text-[12px] leading-[10px]';

const StatCard: FC<StatCardProps> = ({ title, value, growth }) => {
  const roundedGrowth = parseFloat(growth.toFixed(1));
  const isPositive = roundedGrowth >= 0;
  const growthColorClass = isPositive ? GROWTH_CLASSES : GROWTH_DECLINE_CLASSES;

  const growthText = `${isPositive ? '+' : ''}${roundedGrowth}% from last month`;

  return (
    <div className={STAT_CARD_BASE_CLASSES}>
      <p className={TITLE_CLASSES}>{title}</p>
      <h4 className={VALUE_CLASSES}>{value}</h4>
      {growth !== 0 && (
        <span className={`${growthColorClass} text-[12px] leading-[10px]`}>
          {growthText}
        </span>
      )}
    </div>
  );
};

interface StatsOverviewProps {
  quizId: string;
}

const StatsOverview: FC<StatsOverviewProps> = ({ quizId }) => {
  const [data, setData] = useState<TQuizStatsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    AnalyticsService.fetchQuizStatsOverview({ quizId })
      .then((res: { data: { data: TQuizStatsOverview | null } }) => {
        setData(res.data?.data ?? null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [quizId]);

  if (loading) {
    return (
      <div className="grid gap-4 mb-2 mt-5 pr-3 pl-5 w-full grid-cols-2">
        {[1, 2].map((i) => (
          <div key={i} className={`${STAT_CARD_BASE_CLASSES} animate-pulse bg-gray-100`} />
        ))}
      </div>
    );
  }

  const stats: StatCardProps[] = data
    ? [
        {
          title: 'Active Students',
          value: data.activeStudents,
          growth: data.activeStudentsGrowth,
        },
        {
          title: 'Quiz Adoption',
          value: `${data.quizAdoptionRate}%`,
          growth: data.adoptionRateGrowth,
        },
      ]
    : [];

  return (
    <div className="grid gap-4 mb-2 mt-5 pr-3 pl-5 w-full grid-cols-2">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

export default StatsOverview;
